import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Bell, Check, CheckCheck, X, FileSignature, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTIFICA_META = {
  contratto_firmato: {
    icon: FileSignature,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    label: "Contratto firmato",
  },
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "ora";
    if (diff < 3600) return `${Math.floor(diff / 60)}m fa`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h fa`;
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const SuperAdminNotificheBell = ({ token, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchNotifiche = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/superadmin/notifiche?limit=30`, { headers });
      setItems(res.data.items || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      // silent
    }
  }, [token]); // eslint-disable-line

  useEffect(() => {
    fetchNotifiche();
    const t = setInterval(fetchNotifiche, 30000);
    return () => clearInterval(t);
  }, [fetchNotifiche]);

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/superadmin/notifiche/${id}/letta`, {}, { headers });
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, letto: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      toast.error("Errore aggiornamento notifica");
    }
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/superadmin/notifiche/leggi-tutte`, {}, { headers });
      setItems((prev) => prev.map((n) => ({ ...n, letto: true })));
      setUnreadCount(0);
      toast.success("Tutte segnate come lette");
    } catch {
      toast.error("Errore");
    }
    setLoading(false);
  };

  const removeOne = async (id) => {
    try {
      await axios.delete(`${API}/superadmin/notifiche/${id}`, { headers });
      setItems((prev) => prev.filter((n) => n.id !== id));
      // Recount unread
      fetchNotifiche();
    } catch {
      toast.error("Errore");
    }
  };

  const handleNotificaClick = (n) => {
    if (!n.letto) markRead(n.id);
    if (n.tipo === "contratto_firmato" && onNavigate) {
      onNavigate("venditori");
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        data-testid="sa-notifiche-bell"
        aria-label="Notifiche"
      >
        <Bell className={`h-5 w-5 ${unreadCount > 0 ? "text-indigo-600" : "text-gray-500"}`} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
            data-testid="sa-notifiche-badge"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-w-[calc(100vw-32px)] bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 overflow-hidden"
          data-testid="sa-notifiche-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-br from-slate-50 to-white">
            <div>
              <h3 className="text-sm font-bold text-gray-900">Notifiche</h3>
              {unreadCount > 0 && (
                <p className="text-[11px] text-gray-500">{unreadCount} non lette</p>
              )}
            </div>
            {items.length > 0 && unreadCount > 0 && (
              <button
                onClick={markAllRead}
                disabled={loading}
                className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                data-testid="sa-mark-all-read-btn"
              >
                <CheckCheck className="h-3 w-3" /> Segna tutte
              </button>
            )}
          </div>

          {/* Lista */}
          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">Nessuna notifica</p>
              </div>
            ) : (
              items.map((n) => {
                const meta = NOTIFICA_META[n.tipo] || { icon: Bell, color: "text-gray-500", bg: "bg-gray-50", label: n.tipo };
                const Icon = meta.icon;
                return (
                  <div
                    key={n.id}
                    onClick={() => handleNotificaClick(n)}
                    className={`group flex gap-3 px-4 py-3 border-b border-gray-50 cursor-pointer transition-colors ${
                      n.letto ? "bg-white hover:bg-gray-50" : "bg-indigo-50/40 hover:bg-indigo-50"
                    }`}
                    data-testid={`sa-notifica-${n.id}`}
                  >
                    <div className={`w-9 h-9 rounded-full ${meta.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`h-4 w-4 ${meta.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-gray-900">
                            {n.venditore_nome || "—"}
                          </p>
                          <p className="text-[11px] text-gray-600 leading-tight">
                            ha firmato il contratto commerciale
                          </p>
                          {n.codice_venditore && (
                            <p className="text-[10px] text-gray-400 font-mono mt-0.5">
                              {n.codice_venditore}
                            </p>
                          )}
                          <p className="text-[10px] text-gray-400 mt-1">
                            {formatDate(n.created_at)}
                          </p>
                        </div>
                        {!n.letto && (
                          <span className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0 mt-1" />
                        )}
                      </div>
                    </div>
                    {/* Azioni hover */}
                    <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {!n.letto && (
                        <button
                          onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                          className="p-1 rounded hover:bg-emerald-100 text-emerald-600"
                          title="Segna come letta"
                          data-testid={`sa-mark-read-${n.id}`}
                        >
                          <Check className="h-3 w-3" />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); removeOne(n.id); }}
                        className="p-1 rounded hover:bg-red-100 text-red-500"
                        title="Cancella"
                        data-testid={`sa-delete-notif-${n.id}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => { if (onNavigate) onNavigate("venditori"); setOpen(false); }}
                className="text-[11px] font-semibold text-gray-600 hover:text-gray-900 flex items-center gap-1"
                data-testid="sa-open-venditori"
              >
                <ExternalLink className="h-3 w-3" /> Vai a Venditori
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SuperAdminNotificheBell;
